import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Tab } from '@mui/material';
import { CheckBoxComp, ChipStatus, DialogForm, IChipsSettingProps, IDialogActions, ISnackbarAlert, MultipleSelectCheckmarks, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleDTO, AccessRoleFilterDTO, CommentDTO, ResumeDTO, ResumeVacancyDTO, UserDTO, UserFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { dateDiff, formatDateTime } from '../../../shared/utils';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { DynamicComboBoxPair } from '../vacancy/DynamicComboBoxPair';
import { useTranslation } from 'react-i18next';

interface IMinimalResumeMaintenanceProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const MinimalResumeMaintenance: React.FC<IMinimalResumeMaintenanceProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {
    const [sourceList, setSourceList] = useState<ValueListDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);

    const [resumeDTO, setResumeDTO] = useState<ResumeDTO>();
    const [resumeVacancyDTO, setResumeVacancyDTO] = useState<ResumeVacancyDTO>();
    const [commentDTO, setCommentDTO] = useState<CommentDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [selectedTab, setSelectdTab] = useState('1');

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setResumeDTO({
            ...resumeDTO,
            id: '',
            name: '',
            jobTitleId: data.jobTitleId,
            seniorityId: data.seniorityId,
            sourceId: ListId.talentsphere.resume.startSourceId,
            email: '',
            cellPhone: '',
            resume: '',
            resumeFile: '',
            statusId: ListId.talentsphere.resume.competingForTheVacancy,
            linkedin: '',
            personalInformation: '',
            isIncomplete: true,
            companyId: ''
        } as ResumeDTO);

        setCommentDTO({
            id: '',
            commentary: '',
            ownerId: '',
            createdById: '',
        } as CommentDTO);

        if (isOpenMaintenance) {
            handleListGetAll(ListId.talentsphere.resume.sourceId, setSourceList);
            handleUserGetAllAsync(setUserList);

            handleGetPermission(ListId.talentsphere.resumeId, setAccessRolePermissionDTO);
        }
    }, [data, isOpenMaintenance]);

    useEffect(() => {
        if (resumeDTO?.id === '' || resumeDTO?.id === undefined)
            return;

        HandleResumeVacancyAddAsync();
        handleCommentAddAsync();
    }, [resumeDTO?.id]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync', accessRoleFilterDTO, setActionExecuted, setLoading, undefined, setList,);
    }, []);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleUserGetAllAsync = useCallback(async (setUserList: any) => {
        const userFilterDTO: UserFilterDTO = {
            companyId: '',
            peopleId: '',
            userName: '',
            isServiceAccount: false
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleSaveClick = useCallback(async () => {
        if (!resumeDTO) {
            showMessage(t('message.requiredFields'), 'warning');
            return;
        }

        const dto = { ...resumeDTO } as ResumeDTO;

        if (resumeDTO?.id === '' || resumeDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('talentsphere.resume', dto, setResumeDTO, setActionExecuted, setLoading, showMessage);
        }
    }, [resumeDTO]);

    const HandleResumeVacancyAddAsync = useCallback(async () => {
        const dto: ResumeVacancyDTO = {
            vacancyId: data.id,
            resumeId: resumeDTO?.id,
            statusId: ListId.talentsphere.resumeVacancy.statusStartId, //initial status
            customerStatusId: ListId.talentsphere.resumeVacancy.customerStatusId,
            customerKanbanIndex: 0,
            kanbanIndex: 0,
            matchPercentageMandatoryHardSkill: 0,
            matchPercentageOptionalHardSkill: 0,
            matchPercentageMandatorySoftSkill: 0,
            matchPercentageOptionalSoftSkill: 0,
            matchPercentageMandatoryLanguage: 0,
            matchPercentageOptionalLanguage: 0,
            createdById: userInfo.user.id
        };
        GenericService.AddAsync1('talentsphere.ResumeVacancy', dto, setResumeVacancyDTO, setActionExecuted, setLoading, showMessage);
    }, [resumeDTO?.id]);

    const handleCommentAddAsync = useCallback(async () => {
        const dto = {
            id: '',
            commentary: '',
            ownerId: '',
            createdById: '',
            ...commentDTO,
        } as CommentDTO;

        if (commentDTO?.id === '' || commentDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            dto.ownerId = resumeDTO?.id || '';
            GenericService.AddAsync1('infosphere.comment', dto, setCommentDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if (!accessRolePermissionDTO?.allowUpdate) {
                showMessage(t('message.allowUpdate'), 'info');
                return;
            }

            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('infosphere.comment', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [resumeDTO?.id]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {

        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);
    }

    const chipStatus: IChipsSettingProps[] = [
        { value: t('resume.chipStatus.available'), iconName: 'person_add_alt_outlined', iconColor: 'success' },
        { value: t('resume.chipStatus.unavailable'), iconName: 'person_add_disabled_outlined', iconColor: 'error' },
        { value: t('resume.chipStatus.hired'), iconName: 'person_outlined', iconColor: 'primary' },
        { value: t('resume.chipStatus.competingForTheVacancy'), iconName: 'manage_accounts_outlined', iconColor: 'info' },
        { value: t('resume.chipStatus.deletionRequested'), iconName: 'manage_accounts_outlined', iconColor: 'warning' },
    ];

    const actions: IDialogActions[] = [
        { id: 'save', text: t('resume.maintenanceButton.save'), icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'close', text: t('resume.maintenanceButton.close'), icon: 'close_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setSelectdTab(newValue);
    };

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title={t('resume.detailsName')}
                icon='article_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'xl'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                            <TabContext value={selectedTab} >
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                    <TabList onChange={handleChange} aria-label="lab API tabs" variant="scrollable" scrollButtons="auto">
                                        <Tab label={t('resume.tabItem.details')} value="1" />
                                        <Tab label={t('resume.tabItem.resume')} value="2" />
                                    </TabList>
                                </Box>
                                <TabPanel value="1" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={3}>
                                            <TextFieldInput
                                                id='name'
                                                label={t('resume.maintenance.name')}
                                                placeholder={t('resume.maintenance.name')}
                                                value={resumeDTO?.name}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={true}
                                                maxLength={200}
                                                autoFocus={true}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextFieldInput
                                                id='email'
                                                label={t('resume.maintenance.email')}
                                                placeholder={t('resume.maintenance.email')}
                                                value={resumeDTO?.email}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                maxLength={400}
                                                type='email'
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <TextFieldInput
                                                id='cellPhone'
                                                label={t('resume.maintenance.cellPhone')}
                                                placeholder={t('resume.maintenance.cellPhone')}
                                                value={resumeDTO?.cellPhone}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                maxLength={20}
                                                numericOnly={true}
                                                mask="+00 (00) 00000-0000"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <SelectField
                                                id='sourceId'
                                                label={t('resume.maintenance.sourceId')}
                                                placeholder={t('resume.maintenance.sourceId')}
                                                value={resumeDTO?.sourceId}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                valueList={sourceList}
                                                required={true}
                                                hasNoneSelect={false}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextFieldInput
                                                id='linkedin'
                                                label={t('resume.maintenance.linkedin')}
                                                placeholder={t('resume.maintenance.linkedin')}
                                                value={resumeDTO?.linkedin}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={false}
                                                maxLength={200}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextFieldInput
                                                id='commentary'
                                                label={t('comment.maintenance.commentary')}
                                                placeholder={t('comment.maintenance.commentary')}
                                                value={commentDTO?.commentary}
                                                setValue={setCommentDTO}
                                                obj={commentDTO}
                                                multiline={true}
                                                rows={6}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                                <TabPanel value="2" sx={{ ml: -3, mr: -3, mb: -3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={12}>
                                            <TextFieldInput
                                                id='resume'
                                                label={t('resume.maintenance.resume')}
                                                value={resumeDTO?.resume}
                                                setValue={setResumeDTO}
                                                obj={resumeDTO}
                                                required={true}
                                                multiline={true}
                                                rows={20}
                                            />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                            </TabContext>
                        </Grid>
                        {resumeDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={resumeDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label={t('resume.maintenance.createdBy')}
                                        value={userList.find(x => x.id === resumeDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label={t('resume.maintenance.creationDateTime')}
                                        value={formatDateTime(String(resumeDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label={t('resume.maintenance.updatedBy')}
                                        value={userList.find(x => x.id === resumeDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label={t('resume.maintenance.updateDateTime')}
                                        value={formatDateTime(String(resumeDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm >
        </React.Fragment >
    );
};
